<template lang="pug">
pr-wrapper.player(
  ref="wrapper",
  fs,
  :zi="index",
  v-nv:scope="'player'",
  nv-exit-protect,
  @nv-scope-focus.native="focused",
  @nv-scope-blur.native="moved",
  @nv-enter.native="control",
  @nv-back.native="control",
  @nv-fw.native="control",
  @nv-rw.native="control",
  @nv-play.native="control",
  @nv-stop.native="control",
  @nv-pause.native="control",
  @nv-toggle.native="control",
  @nv-info.native="triggerOSD",
  @nv-up.native="showOSD",
  @nv-down.native="closeOSD",
  @nv-exit.native="control"
)
  pr-wrapper.player-control(bg="overlayFrom", bgo=".9", bgd="90", v-visible="osd")
    pr-wrapper.d-flex.control-osd.border.align-items-center.px-4.py-2(bg)
      pr-text.flex-grow-0(size="small") {{ since }}
      .flex-grow-1.px-3
        progress-bar.bar(v-bind="progress")
      pr-text.flex-grow-0(size="small") {{ left }}
    pr-wrapper.mt-2.text-center
      .d-inline-flex.align-items-center.justify-content-center
        pr-button.border-right.themed.py-2.px-3(v-nv, outline, small, nv-el-fov="90", @nv-enter.native="control({ type: 'nv-rw' })")
          icon(:icon="'fas backward'") 
        pr-button#trigger.border-right.themed.py-2.px-3.mx-3(
          v-nv.current,
          outline,
          small,
          nv-el-fov="90",
          @nv-enter.native="control({ type: 'nv-toggle' })"
        )
          icon(:icon="'fas ' + icon") 
        pr-button.border-right.themed.py-2.px-3(v-nv, outline, small, nv-el-fov="90", @nv-enter.native="control({ type: 'nv-fw' })")
          icon(:icon="'fas forward'") 

  .media-error.shadow(v-if="mediaError")
    pr-text(size="small") {{ mediaError }}

  .preloader(v-if="preloaderEnable", :class="{ active: preloader }")
    icon.fa-spin(:icon="'fas spinner'") 
</template>

<script>
export default {
  name: "player",
  data() {
    return {
      isPlaying: false,
      isSeekable: false,
      mediaError: false,
      preloader: false,
      index: "1",
      osd: false,
      intent: false,
      interval: null,
      timeout: null,
      duration: 0,
      current: 0,
      nextPosition: 0,
      icon: "play"
    }
  },
  computed: {
    preloaderEnable() {
      return window.preloader !== false
    },
    since() {
      return _$.duration(this.current)
    },
    left() {
      return _$.duration(this.duration - this.current)
    },
    progress() {
      return {
        value: Math.floor((this.current / this.duration) * 100),
        diff: Math.floor((this.nextPosition / this.duration) * 100),
        label: this.nextPosition ? _$.duration(this.nextPosition) : ""
      }
    }
  },
  watch: {
    isPlaying() {
      this.isSeekable = false
      this.duration = 0
      this.current = 0
      this.nextPosition = 0
    }
  },
  mounted() {
    _$.vue.$on("media", this.start)
    _$.vue.$on("mediaError", this.error)
    _$.vue.$on("mediaPlaying", this.playing)
    _$.vue.$on("mediaEnded", this.ended)
    _$.vue.$on("mediaStopped", this.stopped)
    _$.vue.$on("mediaPaused", this.paused)
    _$.vue.$on("playerMetrics", this.metrics)
    _$.vue.$on("source", this.sourceChanged)

    _$.nav.sync("player")
  },
  beforeDestroy() {
    _$.vue.$off("media", this.start)
    _$.vue.$off("mediaError", this.error)
    _$.vue.$off("mediaPlaying", this.playing)
    _$.vue.$off("mediaEnded", this.ended)
    _$.vue.$off("mediaStopped", this.stopped)
    _$.vue.$off("mediaPaused", this.paused)
    _$.vue.$off("playerMetrics", this.metrics)
    _$.vue.$off("source", this.sourceChanged)

    _$.vue.$off("sleep", this.closeOSD)
  },
  methods: {
    sourceChanged() {
      if (__.source.isExternal) this.preloader = false
    },
    /* Player Helpers */
    control(event) {
      if (typeof event !== "object" || !event.type) return false
      if (!this.osd && event.type !== "nv-back") return this.showOSD()

      switch (event.type) {
        case "nv-fw":
          this.controlForward()
          break
        case "nv-rw":
          this.controlRewind()
          break
        case "nv-play":
          this.controlPlay()
          break
        case "nv-pause":
          this.controlPause()
          break
        case "nv-stop":
        case "nv-exit":
          this.controlStop()
          break
        case "nv-toggle":
          this.controlToggle()
          break
        case "nv-back":
          this.controlBack()
          break
      }
    },
    async controlToggle() {
      if (this.timeout) clearTimeout(this.timeout)

      if (this.intent) return false
      this.intent = true

      if (this.nextPosition && this.nextPosition !== this.current) {
        await this.controlSetPosition()
      } else {
        let res = await __.media.pause()
        if (!res) res = await __.media.continue()
      }

      this.intent = false
      return true
    },
    controlBack() {
      if (this.timeout) clearTimeout(this.timeout)
      if (this.nextPosition) this.nextPosition = 0
      else if (this.osd) this.closeOSD()
      else this.controlStop()
      return true
    },
    controlPause() {
      if (this.nextPosition) this.nextPosition = 0
      if (this.intent) return false
      this.intent = true

      __.media.pause().finally(() => (this.intent = false))

      return true
    },
    controlPlay() {
      if (this.nextPosition) this.nextPosition = 0
      if (this.intent) return false
      this.intent = true

      __.media.continue().finally(() => (this.intent = false))

      return true
    },
    async controlStop() {
      if (this.nextPosition) this.nextPosition = 0
      if (this.intent) return false
      if (__.media.dvr) {
        const res = await _$.vue.$interact.confirm(_$.vue.$t("Are you sure you want to return to live?"), null, "left")
        if (!res) return false
      }

      this.intent = true

      if (this.osd) this.closeOSD()
      __.media.stop(true).finally(() => (this.intent = false))

      return true
    },
    controlForward() {
      if (!this.nextPosition) this.nextPosition = this.current
      if (this.nextPosition + 30 >= this.duration) return false
      this.nextPosition += 30

      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(() => this.controlToggle(), 2000)
    },
    controlRewind() {
      if (!this.nextPosition) this.nextPosition = this.current
      if (this.nextPosition - 30 <= 0) return false
      this.nextPosition -= 30

      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(() => this.controlToggle(), 2000)
    },
    async controlSetPosition() {
      const res = await __.media.setPosition(this.nextPosition)
      if (res) this.current = this.nextPosition
      this.nextPosition = 0

      return true
    },

    /* Player OSD */
    focused() {
      this.showOSD()
    },
    moved() {
      this.closeOSD()
    },
    showOSD() {
      if (_$.nav.currentScopeName() !== "player" || this.osd || !this.isSeekable) return true
      this.osd = true
      _$.nav.findAndSet("trigger")
      _$.vue.$on("sleep", this.closeOSD)
    },
    closeOSD() {
      this.osd = false
      this.nextPosition = 0
      _$.vue.$off("sleep", this.closeOSD)
    },
    triggerOSD() {
      if (this.osd) this.closeOSD()
      else this.showOSD()
    },

    /* Player Listeners */
    changePlayingStatus(val = !this.isPlaying) {
      this.isPlaying = val

      if (val) this.mediaError = false
      else {
        if (this.osd) this.closeOSD()
        if (this.interval) clearInterval(this.interval)
      }

      this.icon = val ? "pause" : "play"
    },
    start() {
      this.changePlayingStatus(false)
      this.mediaError = false
      this.preloader = true
    },
    error() {
      if (!__.media.hasPlayer()) return false
      __.media.status = "error"
      this.mediaError = this.$t("Media is not available...")

      this.changePlayingStatus(false)
      this.preloader = false
    },
    playing() {
      __.media.status = "playing"
      this.changePlayingStatus(true)
      this.preloader = false

      if (this.interval) clearInterval(this.interval)

      _$.sleep(1000)
        .then(() => __.media.isSeekable())
        .then(payload => {
          this.isSeekable = payload

          if (!this.isSeekable) return
          __.media.getDuration().then(duration => (this.duration = duration))
          if (!this.current) __.media.getPosition().then(current => (this.current = current))

          if (!this.duration) setTimeout(() => __.media.getDuration().then(duration => (this.duration = duration)), 3000)
          this.interval = setInterval(() => {
            if (this.current >= this.duration) return false
            this.current++
          }, 1000)

          this.showOSD()
        })
    },
    ended() {
      __.media.status = "ended"
      this.changePlayingStatus(false)
    },
    stopped() {
      __.media.status = "stopped"
      this.changePlayingStatus(false)
      this.preloader = false
    },
    paused() {
      if (!__.media.isActive()) return
      __.media.status = "paused"
      this.changePlayingStatus(true)
      this.icon = "play"

      if (this.interval) clearInterval(this.interval)
    },
    metrics(data) {
      const { width, height, x, y, index } = data

      this.index = index ? "4" : "1"
      this.$refs.wrapper.$el.style.width = width + "px"
      this.$refs.wrapper.$el.style.height = height + "px"
      this.$refs.wrapper.$el.style.left = x + "px"
      this.$refs.wrapper.$el.style.top = y + "px"

      return true
    }
  }
}
</script>

<style lang="scss">
.player {
  display: flex;

  .player-control {
    position: absolute;
    z-index: 3;
    width: 100%;
    padding: 3rem 3rem 1rem;
    bottom: 0;
    overflow: initial;
    .control-osd {
      overflow: initial;
    }
  }

  .temp-nv-element {
    position: absolute;
    z-index: 3;
    left: -1280px;
    top: 0;
  }
  .media-error {
    color: white;
    background: black;
    text-align: center;
    white-space: nowrap;
    position: absolute;
    top: 50%;
    left: 50%;
    padding: 1rem 2rem;
    border-radius: 5px;
    z-index: 2;
    /* bring your own prefixes */
    transform: translate(-50%, -50%);
  }

  .preloader {
    color: white;
    text-align: center;
    position: absolute;
    top: 3%;
    right: 3%;
    border-radius: 5px;
    visibility: hidden;
    z-index: 1;
    background: black;
    padding: 0.5rem 0.75rem;

    &.active {
      visibility: visible;
    }
  }

  #videoplane,
  #videoplane_js,
  #pluginPlayer {
    position: absolute;
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &.VideoJS {
      video {
        object-fit: fill !important;
      }
    }
  }
}
</style>
